import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { RootState } from 'src/reducers/RootReducer';
import { getImage, GetImageAction, GetImageSuccess } from 'src/actions/image/get/GetImage.actions';
import ProjectTeamMember from 'src/value-objects/ProjectTeamMembers';
import ProjectUpdateTeamMemberCardComponent, { ProjectUpdateTeamMemberCardComponentProps }
  from './ProjectUpdateTeamMemberCard.component';
import Base64Encoder from 'src/services/encoders/Base64Encoder';
import * as updateProjectTeamMemberTypes from 'src/actions/project/team-member/update/Types';
import { changeProjectData, ChangeProjectDataAction } from 'src/actions/project/change-data/ChangeProjectData.actions';
import {
  updateProjectTeamMember,
  UpdateProjectTeamMemberAction,
  UpdateProjectTeamMemberSuccess
} from 'src/actions/project/team-member/update/UpdateProjectTeamMember.actions';
import {
  deleteProjectTeamMember,
  DeleteProjectTeamMemberAction
} from 'src/actions/project/team-member/delete/DeleteProjectTeamMember.actions';
import * as deleteProjectTeamMemberTypes from 'src/actions/project/team-member/delete/Types';
import ProjectData from 'src/value-objects/ProjectData';

interface StateProps {
  errorMessage: {};
}

interface DispatchProps {
  getImage(imageName: string): Promise<GetImageAction>;

  updateProjectTeamMember(projectId: number, teamMember: ProjectTeamMember): Promise<UpdateProjectTeamMemberAction>;

  deleteProjectTeamMember(projectId: number, teamMemberId: number): Promise<DeleteProjectTeamMemberAction>;

  changeProjectData(project: ProjectData): ChangeProjectDataAction;
}

interface State {
  profilePicture?: File;
  editTeamMember: boolean;
  formData: ProjectTeamMember;
}

interface Props extends StateProps, DispatchProps {
  project: ProjectData;
  teamMember: ProjectTeamMember;
}

class ProjectUpdateTeamMemberCardContainer extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      editTeamMember: false,
      formData: new ProjectTeamMember(this.props.teamMember)
    };
  }

  public componentWillMount() {
    if (this.props.teamMember.profilePicture && !this.props.teamMember.profilePicture.encoded) {
      this.props.getImage(this.props.teamMember.profilePicture.url).then((response: GetImageSuccess) => {
        if (response.type === 'GET_IMAGE_SUCCESS') {
          this.setState({profilePicture: response.payload});
        }
      });
    }
  }

  public render(): React.ReactNode {
    return (
      <ProjectUpdateTeamMemberCardComponent {...this.getComponentProps()} />
    );
  }

  private editTeamMember(): void {
    this.setState({
      editTeamMember: true
    });
  }

  private deleteTeamMember(): void {
    this.props.deleteProjectTeamMember(this.props.project.id, this.props.teamMember.id!).then((response) => {
      if (response.type === deleteProjectTeamMemberTypes.DELETE_PROJECT_TEAM_MEMBER_SUCCESS) {
        let newData = this.props.project;
        newData.teamMembers.splice(newData.teamMembers.findIndex(e => e.id === this.props.teamMember.id), 1);
        this.props.changeProjectData(new ProjectData(newData));
        if (this.props.teamMember.profilePicture && !this.props.teamMember.profilePicture.encoded) {
          this.props.getImage(this.props.teamMember.profilePicture.url).then((res: GetImageSuccess) => {
            if (res.type === 'GET_IMAGE_SUCCESS') {
              this.setState({profilePicture: res.payload});
            }
          });
        }
      }
    });
  }

  private handleRichEditor(key: string): (html: string) => void {
    return (
      (html: string) => {
        let newData = {...this.state.formData!, [key]: html};
        this.setState({formData: newData});
      }
    );
  }

  private handleChange(key: string): (value: string | File) => void {
    switch (key) {
      case 'profilePicture':
        return (
          (value: File) => {
            Base64Encoder.encode(value).then((result) => {
              let newImage = {
                url: URL.createObjectURL(value),
                encoded: result.split('base64,')[1],
                ext: value.type.split('/')[1],
                name: value.name
              };
              let newData = this.state.formData!;
              newData.profilePicture = newImage;
              this.setState({
                formData: newData,
                profilePicture: undefined,
              });
            });
          }
        );
      default:
        return (
          (value: string) => {
            let newData = {...this.state.formData!, [key]: value};
            this.setState({formData: newData});
          }
        );
    }
  }

  private handleSubmit(): void {
    this.props.updateProjectTeamMember(this.props.project.id, this.state.formData!).then(
      (response: UpdateProjectTeamMemberSuccess) => {
        if (response.type === updateProjectTeamMemberTypes.UPDATE_PROJECT_TEAM_MEMBER_SUCCESS) {
          this.setState({editTeamMember: false});
          let newData = this.props.project;
          let teamMembers = newData.teamMembers.map((item) => {
            return item.id === this.state.formData.id ? this.state.formData : item;
          });
          newData.teamMembers = teamMembers;
          this.props.changeProjectData(new ProjectData(newData));
        }
      }
    );
  }

  private getComponentProps(): ProjectUpdateTeamMemberCardComponentProps {
    return {
      editTeamMember: () => this.editTeamMember(),
      deleteTeamMember: () => this.deleteTeamMember(),
      handleChange: (key: string) => this.handleChange(key),
      handleRichEditor: (key: string) => this.handleRichEditor(key),
      handleSubmit: () => this.handleSubmit(),
      teamMember: this.props.teamMember,
      profilePicture: this.state.profilePicture || undefined,
      edit: this.state.editTeamMember,
      formData: this.state.formData,
      errorMessage: this.props.errorMessage
    };
  }

}

const mapDispatchToProps: (dispatch: Dispatch<RootState>) => DispatchProps = (
  dispatch: Dispatch<RootState>): DispatchProps => ({
  getImage: (imageName: string): Promise<GetImageAction> => (
    dispatch(getImage(imageName))
  ),
  updateProjectTeamMember: (projectId: number, teamMember: ProjectTeamMember):
    Promise<UpdateProjectTeamMemberAction> => (
    dispatch(updateProjectTeamMember(projectId, teamMember))
  ),
  deleteProjectTeamMember: (projectId: number, teamMemberId: number): Promise<DeleteProjectTeamMemberAction> => (
    dispatch(deleteProjectTeamMember(projectId, teamMemberId))
  ),
  changeProjectData: (project: ProjectData): ChangeProjectDataAction => (
    dispatch(changeProjectData(project))
  )
});

const mapStateToProps: (state: RootState) => StateProps = (state: RootState): StateProps => ({
  errorMessage: state.updateProjectTeamMember.errorMessage,
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectUpdateTeamMemberCardContainer);



// WEBPACK FOOTER //
// ./src/modules/project/update/partials/round-information/ProjectUpdateTeamMemberCard.container.tsx